import React, {FC} from "react"
import PropTypes from "prop-types"

import {IconTypes} from "@irati/ui/src/Icon"
import {useTheme} from "@irati/ui/src/global/useTheme"
import Flex from "@irati/ui/src/Flex"
import {Body} from "@irati/ui/src/Typography"
import FormattedMessage, {InjectIntl, injectIntl} from "@irati/utils/lang"
import {PaymentCurrencyType, ReportingHighestSavingPerks, ReportingHighestSavingPerk} from "@irati/types"
import {formatCurrency} from "@irati/utils/currency/formatCurrency"
import history from "@irati/utils/history"
import {Tooltip} from "@irati/ui/src/Tooltip"
import {adminDashboardTracking} from "@irati-tracking/admin"
import Widget from "./Widget"
import {Image, ImageWrapper, Wrapper} from "./styled"
import IconTextSavings from "./IconTextSavings"
import BoldNumberSmallText from "./BoldNumberSmallText"

interface Props extends InjectIntl {
  highestSavingPerks: ReportingHighestSavingPerks
  currencyCode: PaymentCurrencyType
}

const HighestSavingPerksWidget: FC<Props> = ({highestSavingPerks, currencyCode, formatMessage}, context) => {
  const theme = useTheme()
  let topPerks = highestSavingPerks?.flexi
  if (topPerks?.length < 3) {
    topPerks = [...topPerks, ...highestSavingPerks.perks.slice(0, 3 - topPerks.length)]
  }

  const buttonText =
    topPerks?.length > 0
      ? "admin.components.dashboard.reporting.highestSavingPerks.button"
      : "admin.components.dashboard.reporting.highestSavingPerks.buttonAll"

  const handleClick = () => {
    adminDashboardTracking.highestSavingPerksInsights()
    history.push(`/analytics/perks/perksSummary`, context)
  }

  const getSavingsValue = (perk: ReportingHighestSavingPerk) => {
    const isPerkSavingEstimated = perk.type === "% discount voucher" || perk.type === "discount voucher"
    let value

    if (isPerkSavingEstimated) {
      value = currencyCode === PaymentCurrencyType.Gbp ? perk.estimatedSavingsGBP : perk.estimatedSavingsAUD
    } else {
      value = currencyCode === PaymentCurrencyType.Gbp ? perk.savingsGBP : perk.savingsAUD
    }
    return value || 0
  }

  return (
    <Widget
      title="admin.components.dashboard.reporting.highestSavingPerks.title"
      icon={IconTypes.wallet}
      buttonText={buttonText}
      onClick={handleClick}
      iconColor={theme.colors.tertiary.y_400}>
      {topPerks?.length > 0 ? (
        <>
          <BoldNumberSmallText
            boldValue={formatCurrency({
              value: topPerks?.length && topPerks?.length > 0 ? getSavingsValue(topPerks[0]) : undefined,
              currencyCode,
              isCents: false
            })}
            text={formatMessage(
              {id: "admin.components.dashboard.reporting.highestSavingPerks.topSaved"},
              {brandName: topPerks[0]?.deal?.brandName || topPerks[0]?.deal?.header}
            )}
          />
          <Flex marginTop={2} justifyContent="space-around">
            {topPerks?.map((perk: ReportingHighestSavingPerk) => {
              const savings = getSavingsValue(perk)

              return (
                <Tooltip tooltipChildren={perk?.deal?.brandName || perk?.deal?.header}>
                  <div key={`${perk.uuid}-highestSavingPerks`}>
                    <Wrapper size={50}>
                      <ImageWrapper size={50}>{perk?.deal?.cardImage && <Image src={perk.deal.cardImage} height="50px" />}</ImageWrapper>
                    </Wrapper>
                    <Flex>
                      <IconTextSavings
                        type={perk.type}
                        text="admin.components.dashboard.reporting.highestSavingPerks.savings"
                        amount={Math.round(savings)}
                        currencyCode={currencyCode}
                        isTextLabel
                      />
                    </Flex>
                  </div>
                </Tooltip>
              )
            })}
          </Flex>
        </>
      ) : (
        <>
          <Flex justifyContent="center">
            <img height={100} width={100} src={formatMessage({id: "admin.components.dashboard.reporting.highestSavingPerks.img"})} />
          </Flex>
          <Body small marginBottom={2}>
            <FormattedMessage id="admin.components.dashboard.reporting.highestSavingPerks.comingSoon.text" />
          </Body>
        </>
      )}
    </Widget>
  )
}

HighestSavingPerksWidget.contextTypes = {
  appPath: PropTypes.string.isRequired
}

export default injectIntl(HighestSavingPerksWidget)
